import "antd/dist/antd.css";
import "../App.css";

import GuideArticle from "../components/GuideArticle";
import GuideSection from "../components/GuideSection";
import GuideStackLead from "../components/GuideStackLead";
import GuideThreeJobs from "../components/GuideThreeJobs";
import GuideCtaBand from "../components/GuideCtaBand";
import CtaCard from "../components/CtaCard";
import RelatedGuides from "../components/RelatedGuides";
import usePageMeta from "../usePageMeta";
import { GUIDES } from "../data/guides";

const META = GUIDES.find((g) => g.slug === "cycle-navigation-tools")!;

// Category accents pulled from the brand --gt-feature-* palette rather
// than the old bright blues/oranges — see the design handoff's category
// colour mapping.
const CATEGORY_ACCENT: Record<string, { fill: string; chipBg: string; chipText: string }> = {
    cycle: { fill: "#7a4f6d", chipBg: "rgba(122,79,109,.14)", chipText: "#5f3c54" },
    general: { fill: "#5a6772", chipBg: "rgba(90,103,114,.13)", chipText: "#485460" },
    cyclehead: { fill: "#0f4c5c", chipBg: "rgba(15,76,92,.12)", chipText: "#0f4c5c" },
};

type Tool = {
    name: string;
    tagline: string;
    cost: string;
    strengths: string;
    weaknesses: string;
    bestFor: string;
    url?: string;
};

type Category = {
    id: keyof typeof CATEGORY_ACCENT;
    heading: string;
    eyebrow: string;
    intro: string;
    tools: Tool[];
};

const CATEGORIES: Category[] = [
    {
        id: "cycle",
        heading: "Cycle-specific apps",
        eyebrow: "Built for the drop bar",
        intro:
            "Written for cycling first. Route quality here means climb profile, surface type and food stops — not corner count.",
        tools: [
            {
                name: "komoot",
                tagline: "The default for touring cyclists.",
                cost: "Region packs from ≈ £4 · World pack £30 one-off",
                url: "https://www.komoot.com",
                strengths:
                    "The web planner is best-in-class for surface-aware routing. Region packs cover the world once for £30 — you own them forever. Voice guidance in a Bluetooth headset works well. The Groups feature is genuinely useful for a small crew. Cycling profiles (road, gravel, MTB, e-bike) all feel first-class rather than bolted-on.",
                weaknesses:
                    "The default 'road bike' profile is aggressive about gravel connectors that aren't fun on 25mm tyres. Adjust the profile if you're on skinny rubber.",
                bestFor:
                    "Multi-day cycle tours where you want elevation, surface and food stops in a single plan.",
            },
            {
                name: "Ride with GPS",
                tagline: "US-origin, huge in the Anglosphere.",
                cost: "Free tier · Premium ≈ £65/year",
                url: "https://ridewithgps.com",
                strengths:
                    "Group ride 'events' with automatic notifications to attendees are excellent for a club or weekend group. Cue sheets that show at the top of the screen while you ride. Offline in Premium. Route-library sharing culture is strong — most local clubs post their routes here.",
                weaknesses:
                    "Premium is where the real navigation lives — the free tier is deliberately limited. UI feels dated. Elevation-profile detail can lag komoot's.",
                bestFor:
                    "Group rides that need pre-shared cue sheets and a live 'am I on route' check.",
            },
            {
                name: "Strava (Routes)",
                tagline: "Not just tracking — the routes tool is underused.",
                cost: "Free tier · Premium ≈ £55/year",
                url: "https://strava.com",
                strengths:
                    "Uses segment popularity to weight route choice, so a Strava-planned route tends to be one lots of locals already ride. Turn-by-turn in the app on Premium. Sync to a Garmin Edge or Wahoo takes one tap. Heatmaps are genuinely useful for finding local roads.",
                weaknesses:
                    "Turn-by-turn navigation is Premium-only. The routes tool is buried in the UI. Popular-route bias sometimes over-favours the same climb everyone posts.",
                bestFor: "Riders exploring a new area who want to piggy-back on local knowledge.",
            },
            {
                name: "Bikemap",
                tagline: "Less known outside Europe, sneaky-good.",
                cost: "Free tier · Premium ≈ £30/year",
                url: "https://www.bikemap.net",
                strengths:
                    "Vast community route database, especially in DACH countries (Germany, Austria, Switzerland) and the Netherlands. Offline in the Premium tier. Gravel and city-cycle modes are both solid. Reasonable free tier for occasional use.",
                weaknesses:
                    "English-language content is thinner than the German-first UI suggests. Some corners of the UI feel busy.",
                bestFor:
                    "City commutes and gravel exploration, particularly in Germany, Austria, the Netherlands and Denmark.",
            },
        ],
    },
    {
        id: "general",
        heading: "General-purpose maps",
        eyebrow: "Mainstream defaults",
        intro:
            "Not built for cycling, but occasionally the most convenient thing when you're topping up a bottle at a supermarket you don't want to lose your way back from.",
        tools: [
            {
                name: "Google Maps",
                tagline: "Everyone's default.",
                cost: "Free",
                url: "https://maps.google.com",
                strengths:
                    "Bicycle mode is genuinely usable in cities — it knows most cycle-path networks in the UK, EU and US. Directions almost anywhere. Live traffic. Offline maps by area.",
                weaknesses:
                    "Bicycle routing outside cities is patchy — will happily send you down a dirt track that turns to mud. No elevation profile. No sense of climb difficulty.",
                bestFor:
                    "City navigation, restaurant lookups on tour, backup when the cycling app can't find the hotel.",
            },
            {
                name: "Apple Maps",
                tagline: "The iPhone-native option, now much better than it was.",
                cost: "Free",
                strengths:
                    "Very readable turn cards on CarPlay and phone. Bicycle mode with elevation profile in most EU/US cities. Look-Around imagery helps with tricky junctions.",
                weaknesses:
                    "Rural cycling routing is thin. Doesn't handle multi-day route planning. Missing detail in the far Alps.",
                bestFor:
                    "iPhone-committed cyclists who want the OS-native experience for city-scale navigation.",
            },
            {
                name: "HERE WeGo",
                tagline: "The 'everyone forgets this exists' app.",
                cost: "Free",
                url: "https://wego.here.com",
                strengths:
                    "Best free offline maps in the business. Country-sized downloads that survive an entire tour on one card. Simple UI, functional bike routing.",
                weaknesses:
                    "Feature-light on the planning side. No community route library. No elevation profiles.",
                bestFor: "Offline backup, or trips where roaming costs.",
            },
        ],
    },
    {
        id: "cyclehead",
        heading: "Cycle head units",
        eyebrow: "Bar-mounted, purpose-built",
        intro:
            "Turn-by-turn plus every metric that matters — power, heart rate, cadence, radar — without draining a phone or leaning on your data plan.",
        tools: [
            {
                name: "Garmin Edge 540–1050",
                tagline: "The category leader, whole family.",
                cost: "≈ £280–640",
                url: "https://www.garmin.com/en-GB/c/sports-fitness/cycling-gps/",
                strengths:
                    "Best-in-class cycling nav, deep integration with power meters, radar and Di2/AXS. Multi-day battery, robust GPX import, an ecosystem every bike shop knows.",
                weaknesses:
                    "Functional-not-beautiful UI with a learning curve; the 1050 passes £600; solar premiums are steep.",
                bestFor:
                    "Cyclists who race, train with power, or want the most robust computer going.",
            },
            {
                name: "Wahoo Elemnt Bolt / Roam",
                tagline: "Button-driven, phone-first setup.",
                cost: "≈ £280–380",
                url: "https://www.wahoofitness.com/devices/bike-computers",
                strengths:
                    "Setup from the app in five minutes, no menu tree. Easy to read in sun, buttons work through any glove, effortless komoot and RWGPS integration.",
                weaknesses:
                    "No touchscreen, so map interaction is limited; turn-by-turn feels less rich than Garmin's; slower firmware cadence.",
                bestFor: "Cyclists who want the essentials done well without wading through menus.",
            },
            {
                name: "Hammerhead Karoo 3",
                tagline: "The best-looking maps in the class.",
                cost: "≈ £400",
                url: "https://www.hammerhead.io",
                strengths:
                    "Full-colour high-res display makes maps read like a phone. Android-based, so third-party integrations feel first-class; features ship fast.",
                weaknesses:
                    "Shorter battery than Garmin/Wahoo; you'll top up more often on tour; the ecosystem still feels smaller post-SRAM.",
                bestFor: "Riders who value map quality above raw metric depth.",
            },
            {
                name: "Bryton Rider 460 / 750",
                tagline: "The budget alternative most don't know exists.",
                cost: "≈ £120–280",
                url: "https://www.brytonsport.com",
                strengths:
                    "Full turn-by-turn at Edge-130 money. Solid multi-day battery, clean sensor pairing, improving app.",
                weaknesses:
                    "Dated UI, thinner companion app, slow firmware, thinner UK dealer support.",
                bestFor:
                    "Turn-by-turn and metrics without Garmin money — commuters and first-time buyers.",
            },
        ],
    },
];

function CycleNavigationGuide() {
    usePageMeta({
        title:
            "Cycle navigation tools — what to actually use in 2026 | Planamoto",
        description:
            "The honest map of what to plan a cycle tour with — komoot, Ride with GPS, Strava, Bikemap, Google Maps, Garmin Edge, Wahoo Elemnt, Hammerhead Karoo, Bryton — with the trade-offs that matter on a multi-day ride.",
        url: "https://www.planamoto.com/guides/cycle-navigation-tools",
    });

    return (
        <GuideArticle
            eyebrow="Guide"
            title="Cycle navigation tools — what to actually use in 2026"
            lede="Plan at home, turn-by-turn on the road, share the plan across the group — three jobs of a routing tool. Most do one or two well. Almost none do all three."
            heroImage={{
                src: META.image!.src,
                alt: META.image!.alt ?? "Wahoo Elemnt head unit mounted on drop-bar handlebars showing a route",
            }}
            beforeSections={
                <>
                    <GuideStackLead
                        authorNote="Spencer · on the touring bike"
                        heading="What I actually run"
                        intro="Same story as the bike: no single app does all three jobs, so I run a small stack — plan in Strava or komoot, sync to a head unit, share the GPX, and keep a paper cue sheet for when it all falls over."
                        columns={[
                            {
                                label: "01 · Plan",
                                title: "Strava or komoot",
                                body: "Most routes start in one of the two — whichever already has the local heatmap or the food stops mapped. Export GPX either way.",
                            },
                            {
                                label: "02 · Navigate",
                                title: "Wahoo Elemnt",
                                body: "Never let me down on tour, and the screen contrast is genuinely great in direct sun. The phone stays in a pocket for photos, not draining on the bars.",
                            },
                            {
                                label: "03 · Share",
                                title: "GPX + komoot Groups",
                                body: "Waypoints at every fork, a live-position layer so I know who fell behind, and an agreed coffee stop 25km in.",
                            },
                        ]}
                        backstop="a printed cue sheet in the jersey pocket — route, overnight address, everyone's number. Planamoto's export does it. Batteries fail; paper doesn't."
                    />
                    <div className="gt-guide-transition">
                        <div className="gt-eyebrow gt-guide-transition__eyebrow">
                            The full field
                        </div>
                        <h2 className="gt-guide-transition__heading">
                            That's my stack. Here's everything else, honestly.
                        </h2>
                        <p className="gt-guide-transition__intro">
                            Every tool worth considering, grouped by what it's for — with
                            the trade-offs that matter on a multi-day ride.
                        </p>
                    </div>
                </>
            }
            articleSchema={{
                slug: META.slug,
                datePublished: META.datePublished,
                dateModified: META.dateModified,
                image: META.image?.src,
                description: META.summary,
            }}
        >
            <GuideThreeJobs
                heading="The three jobs — and why no one tool nails all three"
                jobs={[
                    {
                        title: "Plan",
                        accent: "#7a4f6d",
                        body: "Building the route on a big screen — elevation, surface, overnight stops at a sensible pedalling distance.",
                    },
                    {
                        title: "Navigate",
                        accent: "#0f4c5c",
                        body: "Turn-by-turn on the head unit, glanceable in bright sun, rerouting when someone takes the wrong lane at a roundabout.",
                    },
                    {
                        title: "Share",
                        accent: "#5b8a72",
                        body: "Getting the plan into everyone's units before you leave. Group rides live and die on this.",
                    },
                ]}
            />
            {CATEGORIES.map((cat, i) => {
                const accent = CATEGORY_ACCENT[cat.id];
                return (
                    <GuideSection
                        key={cat.id}
                        index={i + 1}
                        heading={cat.heading}
                        intro={cat.intro}
                        tools={cat.tools}
                        accent={accent}
                        chipLabel={cat.eyebrow}
                    />
                );
            })}

            <GuideSection
                index={CATEGORIES.length + 1}
                heading="Sharing the plan across the group — honestly"
                intro="Hand four riders the same GPX and four units pick four different lines. Add traffic lights, a puncture and a photo stop and a group of four becomes two pairs in ten minutes. A few things actually work:"
                highlight={{ glyph: "★", background: "var(--gt-accent)", color: "#3a2a0a" }}
                items={[
                    {
                        title: "Mark the junctions, not the roads",
                        body: "Scatter waypoints at every fork, roundabout and unnamed lane where the cycle path leaves the road. Less room to invent, and the units converge.",
                    },
                    {
                        title: "Nominate a leader, agree the regroups",
                        body: "A coffee stop 20–30km in, then lunch, then a viewpoint. Ride your own pace to each. How club rides have always worked.",
                    },
                    {
                        title: "Live-position layer",
                        body: "komoot Groups and Ride with GPS Events show each rider on the route in real time — you know who fell behind, and where.",
                    },
                    {
                        title: "The paper cue sheet",
                        body: "One sheet per day in the jersey pocket — route, overnight address, everyone's number. Planamoto's export is built for it.",
                    },
                ]}
            />

            <GuideSection
                index={CATEGORIES.length + 2}
                columns={[
                    {
                        heading: "Offline, battery & boring truths",
                        items: [
                            {
                                marker: "✓",
                                title: "Load offline maps before the border",
                                body: "Roaming is fine in the EU but slow in the mountains. Google, Apple, HERE, komoot packs and every head unit work without data.",
                            },
                            {
                                marker: "✓",
                                title: "Head unit for nav, phone for photos",
                                body: "Head units are built for multi-day battery; phone nav isn't. Max brightness at noon drains a phone 50% in two hours.",
                            },
                            {
                                marker: "✓",
                                title: "Mount it properly",
                                body: "If the phone is your only nav, a Quad Lock or SP Connect with a silicon sleeve beats a stretchy stem strap in the wet.",
                            },
                        ],
                    },
                    {
                        heading: "File formats",
                        items: [
                            {
                                marker: "GPX",
                                title: "The universal one",
                                body: "Every planner and head unit reads and writes it. Learn this one — plain XML, human-readable.",
                            },
                            {
                                marker: "TCX",
                                title: "Garmin's workout format",
                                body: "Mostly for structured intervals rather than routes. Rarely needed for touring.",
                            },
                            {
                                marker: "FIT",
                                title: "What the unit records to",
                                body: "Holds the ride you did, not the route you planned. Upload to Strava/komoot; keep the GPX for the library.",
                            },
                        ],
                    },
                ]}
            />

            <GuideCtaBand
                heading="We handle the plan. Your head unit handles the turns."
                body="Stops, overnight address, expenses, checklists and the group live in Planamoto. Each day gives you a route link today — per-day GPX export to komoot, Ride with GPS or a Garmin Edge is on the roadmap."
            />

            <RelatedGuides
                slugs={[
                    "motorbike-navigation-tools",
                    "cycle-touring-packing",
                    "fuel-prices",
                    "power-plugs",
                ]}
            />

            <CtaCard
                title="Plan the day, hand off to your head unit"
                body="Once the route's decided, the plan around it — stops, accommodation, expenses across currencies, group checklists — lives in Planamoto. Browse a curated tour to see how a day looks, or start your own."
                primary={{ label: "Browse tours", to: "/explore" }}
            />
        </GuideArticle>
    );
}

export default CycleNavigationGuide;
